import React from 'react';
import { Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Counter, TestConfig } from './types';
import { config } from '../../../config';

interface TestConfigurationProps {
  counters: Counter[];
  loading: boolean;
  testConfig: TestConfig;
  isRunningTest: boolean;
  onChange: (e: React.ChangeEvent<HTMLElement>) => void;
  onStartTest: () => void;
  onStopTest: () => void;
  onApplyDefaults: () => void;
}

const TestConfiguration: React.FC<TestConfigurationProps> = ({
  counters,
  loading,
  testConfig,
  isRunningTest,
  onChange,
  onStartTest,
  onStopTest,
  onApplyDefaults
}) => {
  const { t } = useTranslation('performance');
  const { t: commonT } = useTranslation();
  
  return (
    <Card>
      <Card.Header className="bg-primary text-white">
        <h2 className="h5 mb-0">{t('configuration.title', 'Test Configuration')}</h2>
      </Card.Header>
      <Card.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>{t('configuration.counter', 'Select Counter')}</Form.Label>
            <Form.Select 
              name="counterId"
              value={testConfig.counterId}
              onChange={onChange}
              disabled={isRunningTest || loading}
            >
              {counters.length > 0 ? (
                <>
                  <option value="">{commonT('common.select', 'Select a counter...')}</option>
                  {counters.map(counter => (
                    <option key={counter._id} value={counter._id}>
                      {counter.name}
                    </option>
                  ))}
                </>
              ) : (
                <option value="">{t('configuration.noCounters', 'No active counters available')}</option>
              )}
            </Form.Select>
            {counters.length === 0 && !loading && (
              <Form.Text className="text-muted">
                {t('configuration.noCountersHelp', 'All counters have expired or no counters exist. Create a new counter to run tests.')}
              </Form.Text>
            )}
          </Form.Group>
          
          {/* Getting Started Guide */}
          {counters.length === 0 && !loading && (
            <Alert variant="info" className="mt-3">
              <strong>{commonT('common.gettingStarted', 'Getting Started:')}</strong>
              <ol className="mb-0 mt-2">
                <li>{t('configuration.step1', 'Create a counter with some buttons')}</li>
                <li>{t('configuration.step2', 'Return here and select your counter')}</li>
                <li>{t('configuration.step3', 'Adjust test parameters as needed')}</li>
                <li>{t('configuration.step4', 'Run a single test or test suite')}</li>
              </ol>
            </Alert>
          )}
          
          <Form.Group className="mb-3">
            <Form.Label>{t('configuration.clicksPerSecond', 'Clicks Per Second')}</Form.Label>
            <Form.Control
              type="number"
              name="clicksPerSecond"
              value={testConfig.clicksPerSecond}
              onChange={onChange}
              min={1}
              max={100}
              disabled={isRunningTest}
            />
            <Form.Text className="text-muted">
              {t('configuration.clicksPerSecondHelp', 'Higher values simulate more concurrent users.')}
            </Form.Text>
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>{t('configuration.testDuration', 'Test Duration (seconds)')}</Form.Label>
            <Form.Control
              type="number"
              name="testDuration"
              value={testConfig.testDuration}
              onChange={onChange}
              min={5}
              max={60}
              disabled={isRunningTest}
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>{t('configuration.batchSize', 'Batch Size')}</Form.Label>
            <Form.Control
              type="number"
              name="batchSize"
              value={testConfig.batchSize}
              onChange={onChange}
              min={1}
              max={100}
              disabled={isRunningTest}
            />
            <Form.Text className="text-muted">
              {t('configuration.batchSizeHelp', 'Maximum number of clicks in a batch. Higher values can improve performance but may increase server load.')}
            </Form.Text>
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>{t('configuration.batchInterval', 'Batch Interval (ms)')}</Form.Label>
            <Form.Control
              type="number"
              name="batchInterval"
              value={testConfig.batchInterval}
              onChange={onChange}
              min={100}
              max={5000}
              step={100}
              disabled={isRunningTest}
            />
            <Form.Text className="text-muted">
              {t('configuration.batchIntervalHelp', 'Time between batch requests in milliseconds. Lower values increase throughput but may reduce success rates.')}
            </Form.Text>
          </Form.Group>
          
          <div className="d-flex gap-2">
            {!isRunningTest ? (
              <Button 
                variant="primary" 
                onClick={onStartTest}
                disabled={loading || !testConfig.counterId}
              >
                {t('actions.startTest', 'Start Test')}
              </Button>
            ) : (
              <Button 
                variant="danger" 
                onClick={onStopTest}
              >
                {t('actions.stopTest', 'Stop Test')}
              </Button>
            )}
            <Button 
              variant="outline-primary" 
              onClick={onApplyDefaults}
              disabled={isRunningTest}
            >
              {t('actions.applyDefaults', 'Use Default Settings')}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default TestConfiguration; 